<template>
    <Menu
        as="div"
        class="relative"
    >
        <MenuButton class="flex items-center gap-x-3">
            <span class="sr-only">Open user menu</span>
            <div class="relative">
                <img
                    class="size-10 rounded-full object-cover"
                    :src="user.getAvatar"
                />
                <div
                    v-if="getStatusColourByValue(user.activity_status)"
                    class="absolute right-0.5 bottom-0.5 size-2 rounded-full ring-2 ring-white transition-colors"
                    :class="getStatusColourByValue(user.activity_status)"
                />
            </div>
            <div class="hidden flex-col items-start gap-0.5 lg:flex">
                <span class="text-muval-gray-1">{{ user.getFullName }}</span>
                <span class="text-muval-gray-2 text-xs">{{ user.getTenant?.name }}</span>
                <span class="text-muval-gray-2 hidden text-xs">{{ getStatusNameByValue(user.activity_status) }}</span>
            </div>
            <SvgIcon
                name="system-chevron-down"
                class="text-muval-gray-2 -ml-1 hidden size-5 lg:block"
            />
        </MenuButton>
        <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
        >
            <MenuItems
                class="absolute right-0 z-50 mt-2 min-w-64 origin-top-right rounded-lg border bg-white shadow-sm"
            >
                <MenuItem
                    v-if="!user.isMoveproSuperAdminUser"
                    as="div"
                    class="text-muval-gray-1 flex cursor-pointer items-center gap-x-3 border-b px-4 py-3"
                >
                    <MuvalSelect
                        v-model="userActivityStatus"
                        value-prop="value"
                        :select-options="statusesWithDisabled"
                        rounded
                        option-component="MSUserActivityStatusOption"
                        display-component="MSUserActivityStatusDisplay"
                        class="w-full"
                        :append-to-body="false"
                        :cannot-deselect="true"
                        @click.stop
                    />
                </MenuItem>
                <MenuItem
                    v-for="item in userNavigation"
                    :key="item.name"
                    as="div"
                    class="text-muval-gray-1 hover:bg-muval-gray-6 flex cursor-pointer items-center gap-x-3 px-4 py-3"
                    @click="$router.push(item.to)"
                >
                    <SvgIcon
                        :name="item.icon"
                        class="size-6"
                    />
                    <span>{{ item.name }}</span>
                </MenuItem>

                <MenuItem
                    v-if="user.isMoveproSuperAdmin || user.hasMultipleTenants || user.hasPendingInvites"
                    as="div"
                    class="text-muval-gray-1 hover:bg-muval-gray-6 flex cursor-pointer items-center gap-x-3 border-t px-4 py-3"
                    @click="user.showTenantModal()"
                >
                    <SvgIcon
                        name="system-user-settings"
                        class="size-6"
                    />
                    <span>Swap organisation</span>
                </MenuItem>

                <MenuItem
                    v-if="!user.isMoveproSuperAdminUser && user.hasAnyRole(['MANAGER', 'ADMIN', 'OWNER'])"
                    as="div"
                    class="text-muval-gray-1 hover:bg-muval-gray-6 flex cursor-pointer items-center gap-x-3 border-t px-4 py-3"
                >
                    <SwitchGroup>
                        <MuvalSwitch
                            id="allow_support"
                            v-model="allowSupport"
                            @update:model-value="updateSupport($event)"
                        />
                        <SwitchLabel for="allow_support">Enable support</SwitchLabel>
                    </SwitchGroup>
                </MenuItem>

                <MenuItem
                    as="div"
                    class="text-muval-gray-1 hover:bg-muval-gray-6 flex cursor-pointer items-center gap-x-3 border-t px-4 py-3"
                    @click="logout"
                >
                    <SvgIcon
                        name="system-logout"
                        class="size-6"
                    />
                    <span>Log out</span>
                </MenuItem>
            </MenuItems>
        </transition>
    </Menu>
</template>

<script setup>
import { uniqBy } from 'lodash';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';

const user = useUserStore();
const notificationsStore = useNotificationsStore();
const tenant = useTenantStore();
const router = useRouter();
const queryClient = useQueryClient();

const { statusesWithDisabled, getStatusColourByValue, getStatusNameByValue } = useActivityStatuses();

const userActivityStatus = computed({
    get: () => user.activity_status,
    set: async (activity_status) => {
        console.log(`! updating activity_status to ${activity_status}`);

        const resp = await window.tenantAxios.patch(`/users/${user.data?.id}/activity-status`, {
            activity_status,
        });

        user.activity_status = resp.data.user.activity_status;

        if (activity_status === 'AVAILABLE') {
            notificationsStore.inactive_status_code = null;
            notificationsStore.inactive_status = null;
        }
    },
});

const allowSupport = ref(user.getTenant?.allow_support);

const allUsersNavigation = [{ name: 'Account', to: '/settings/account', icon: 'system-user-outline' }];
const tenantUserNavigation = computed(() => {
    const navigation = [];

    if (user.isOwner || user.isMoveproSuperAdmin || user.isTenantContext) {
        navigation.push({ name: 'Settings', to: '/settings', icon: 'system-toolbox' });
    }

    if (user.isAdmin) {
        navigation.push({ name: 'Settings', to: '/settings', icon: 'system-toolbox' });
    }

    return navigation;
});

const helpNav = [{ name: 'Help', to: '/help', icon: 'system-support' }];

const userNavigation = computed(() => {
    let navigation = [];

    if (user.isTenantContext) {
        navigation = [...navigation, ...tenantUserNavigation.value];
    }

    navigation = [...allUsersNavigation, ...navigation];

    if (!user.isMoveproSuperAdmin) {
        navigation = [...navigation, ...helpNav];
    }

    return uniqBy(navigation, 'name');
});

const updateSupport = async (value) => {
    try {
        await tenant.update(user.getTenant.id, { allow_support: value });
        await user.fetchUser(false);
    } catch (error) {
        console.error('Error updating support setting:', error);
    }
};

async function logout() {
    await user.logout();

    // Clear Vue Query cache to prevent data from previous user session
    queryClient.clear();

    router.replace({ path: '/login' });
}
</script>
